import { memo, useMemo } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { buildEntityBadgeData } from '@/lib/crm/components/badges/entityBadgeHelpers';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { getAvatarInitials } from '@/lib/crm/utils/avatar';
import type { CrmObject } from '@/types/crm';

import {
  containsHtmlTags,
  smartTruncateHtml,
  smartTruncatePlainText,
} from '../../record/notes/NoteCard.subcomponents';
import type { CardRow } from '../utils/cardConfig';
import { type ResolvedSlot, resolveSlot } from '../utils/cardConfigResolution';

const NOTE_BODY_PREVIEW_CHARS = 280;
const NOTE_BODY_PREVIEW_LINES = 4;

// ---------------------------------------------------------------------------
// PropertySlotView — label + value for a single resolved property slot
// ---------------------------------------------------------------------------

const PropertySlotView = memo(function PropertySlotView({
  schema,
  value,
  orgId,
  showLabel,
}: {
  schema: PropertyTypeSchema;
  value: unknown;
  orgId?: number;
  showLabel?: boolean;
}) {
  const label = schema.label || schema.property_type_name;
  const isMarkdown = schema.field_type_name === 'markdown';

  // Markdown (rich-text) values are block content that must WRAP within the
  // card, not render as a single clipped line. Pre-truncate to a fixed
  // character budget with the mention-safe helpers (never cutting an entity
  // tag mid-tag) so a long note doesn't render its full DOM, then render it
  // wrapped — matching the pre-#8032 NoteCardContent excerpt.
  const markdownExcerpt = useMemo(() => {
    if (!isMarkdown) {
      return null;
    }
    const content = String(value ?? '');
    if (!content) {
      return '';
    }
    return containsHtmlTags(content)
      ? smartTruncateHtml(content, NOTE_BODY_PREVIEW_CHARS).html
      : smartTruncatePlainText(
          content,
          NOTE_BODY_PREVIEW_CHARS,
          NOTE_BODY_PREVIEW_LINES
        );
  }, [isMarkdown, value]);

  const valueElement = isMarkdown ? (
    <div className='text-sm text-[var(--color-text-secondary)] min-w-0 max-w-full overflow-hidden whitespace-normal break-words'>
      <PropertyValueLabel
        value={markdownExcerpt}
        propertyType={schema}
        orgId={orgId}
        fontSize='sm'
      />
    </div>
  ) : (
    <div className='text-sm text-[var(--color-text-secondary)] min-w-0 max-w-full truncate'>
      <PropertyValueLabel value={value} propertyType={schema} fontSize='sm' />
    </div>
  );

  if (showLabel) {
    return (
      <div className='flex flex-col gap-0.5 min-w-0'>
        <span className='text-xs font-medium text-[var(--color-text-tertiary)] truncate'>
          {label}
        </span>
        {valueElement}
      </div>
    );
  }

  return <Tooltip title={label}>{valueElement}</Tooltip>;
});

// ---------------------------------------------------------------------------
// AssociationSlotView — avatar + name for a resolved association slot
// ---------------------------------------------------------------------------

const AssociationSlotView = memo(function AssociationSlotView({
  target,
  showLabel,
  label,
}: {
  target: CrmObject;
  showLabel?: boolean;
  label?: string;
}) {
  const data = buildEntityBadgeData(target);
  const valueElement = (
    <div className='flex items-center gap-1.5 min-w-0'>
      <Avatar
        size='xxs'
        src={data.avatarUrl}
        initials={getAvatarInitials(data.displayName)}
      />
      <span className='text-sm font-semibold text-[var(--color-text-secondary)] truncate'>
        {data.displayName}
      </span>
    </div>
  );

  if (showLabel && label) {
    return (
      <div className='flex flex-col gap-0.5 min-w-0'>
        <span className='text-xs font-medium text-[var(--color-text-tertiary)] truncate'>
          {label}
        </span>
        {valueElement}
      </div>
    );
  }

  return valueElement;
});

// ---------------------------------------------------------------------------
// SlotView — dispatch to PropertySlotView or AssociationSlotView
// ---------------------------------------------------------------------------

const SlotView = memo(function SlotView({
  resolved,
  showLabel,
  associationLabel,
  orgId,
}: {
  resolved: ResolvedSlot;
  showLabel?: boolean;
  associationLabel?: string;
  orgId?: number;
}) {
  if (resolved === null) {
    return null;
  }
  if (resolved.kind === 'property') {
    return (
      <PropertySlotView
        schema={resolved.schema}
        value={resolved.value}
        orgId={orgId}
        showLabel={showLabel}
      />
    );
  }
  return (
    <AssociationSlotView
      target={resolved.target}
      showLabel={showLabel}
      label={associationLabel}
    />
  );
});

// ---------------------------------------------------------------------------
// CardRowView — renders one or two side-by-side slots for a single config row
// ---------------------------------------------------------------------------

export const CardRowView = memo(function CardRowView({
  row,
  object,
  propertySchemas,
  associatedObjects,
  associationLabels,
}: {
  row: CardRow;
  object: CrmObject;
  propertySchemas: Record<string, PropertyTypeSchema>;
  associatedObjects: Record<string, CrmObject>;
  associationLabels: Map<string, string>;
}) {
  // Resolve each configured slot, keeping a stable key derived from the slot's
  // position in the (static) config row so keys stay unique even when two slots
  // resolve to the same property or entity.
  const resolvedSlots = useMemo(
    () =>
      row.slots
        .map((slot, index) => ({
          key: `${slot.type}:${slot.name}:${index}`,
          showLabel: slot.showLabel,
          associationLabel:
            slot.type === 'association'
              ? associationLabels.get(slot.name)
              : undefined,
          resolved: resolveSlot(
            slot,
            object,
            propertySchemas,
            associatedObjects
          ),
        }))
        .filter(
          (
            r
          ): r is {
            key: string;
            showLabel: boolean | undefined;
            associationLabel: string | undefined;
            resolved: NonNullable<ResolvedSlot>;
          } => r.resolved !== null
        ),
    [row, object, propertySchemas, associatedObjects, associationLabels]
  );

  if (resolvedSlots.length === 0) {
    return null;
  }

  if (resolvedSlots.length === 1) {
    return (
      <div className='px-3 mt-3 min-w-0'>
        <SlotView
          resolved={resolvedSlots[0].resolved}
          showLabel={resolvedSlots[0].showLabel}
          associationLabel={resolvedSlots[0].associationLabel}
          orgId={object.org_id}
        />
      </div>
    );
  }

  return (
    <div className='flex items-end gap-3 px-3 mt-3 min-w-0'>
      {resolvedSlots.map(item => (
        <div key={item.key} className='flex-1 min-w-0'>
          <SlotView
            resolved={item.resolved}
            showLabel={item.showLabel}
            associationLabel={item.associationLabel}
            orgId={object.org_id}
          />
        </div>
      ))}
    </div>
  );
});
